/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Location } from '@angular/common';

import {
  from,
  of,
  Observable,
  BehaviorSubject,
  combineLatest,
  throwError,
} from 'rxjs';
import {
  tap,
  catchError,
  concatMap,
  shareReplay,
  flatMap,
} from 'rxjs/operators';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Auth0Client, createAuth0Client } from '@auth0/auth0-spa-js';
import { config } from '../../assets/config-constants';

@Injectable()
export class AuthService  {
  private _config;
  private _user = new BehaviorSubject<any>(null);
  get user() {
    return this._user.value;
  }
  user$ = this._user.asObservable();

  // eslint-disable-next-line @typescript-eslint/ban-types
  private _overturUser = new BehaviorSubject<Object>(null);
  get overturUser() {
    return this._overturUser.value;
  }
  overturUser$ = this._overturUser.asObservable();

  public routeFrom: string;
  public routeTo: string;
  auth0Client$: Observable<Auth0Client>;
  isAuthenticated$;
  handleRedirectCallback$;
  loggedIn: boolean = null;
  onboarding = null;
  delayedVerification = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private _location: Location
  ) {
    this._config = environment.alleAuthConfig;

    this.auth0Client$ = (
      from(
        createAuth0Client({
          domain: this._config.domain,
          clientId: this._config.clientID,
          authorizationParams: {
            redirect_uri: this._config.redirectUri,
            audience: this._config.audience,
            scope: this._config.scope
          },
        })
      ) as Observable<Auth0Client>
    ).pipe(
      shareReplay(1), // Every subscription receives the same shared value
      catchError((err) => throwError(err))
    );
    this.isAuthenticated$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.isAuthenticated())),
      tap((res) => {
        this.loggedIn = res;
      })
    );
    this.handleRedirectCallback$ = this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.handleRedirectCallback()))
    );
  }

  setOnboarding(onboarding: boolean) {
    this.onboarding = onboarding;
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean | UrlTree> | boolean {
    return this.isAuthenticated$.pipe(
      tap((loggedIn) => {
        if (!loggedIn) {
          this.login(state.url);
        }
      })
    );
  }

  getTokenSilently$(options?): Observable<any> {
    if (this.onboarding === null) {
      return of('');
    }

    if (this.onboarding) {
      return of(localStorage.getItem('access_token'));
    }

    if (this.onboarding === false) {
      return this.auth0Client$.pipe(
        concatMap((client: Auth0Client) =>
          from(client.getTokenSilently(options))
        )
      );
    }
  }

  getUser$(): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap((user) => {
        if(user){
          user.isInternal = user[config.claimUserType] === config.internalUserClaim; 
          if(user.isInternal){
            this.router.navigate(['auth-error']);
          }
          this._user.next(user)
        }
        })
    );
  }

  public loginRedirect(): void {
    if (this.routeTo) {
      this.router.navigate([this.routeTo]);
    } else {
      this.router.navigate([this._config.inAppRedirects.loggedIn]);
    }
  }

  localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    if (!this.onboarding && this.onboarding !== null) {
      this.isAuthenticated$
        .pipe(
          concatMap((loggedIn: boolean) => {
            if (loggedIn) {
              // If authenticated, get user and set in app
              // NOTE: you could pass options here if needed
              return this.getUser$();
            }
            // If not authenticated, return stream that emits 'false'
            return of(loggedIn);
          })
        )
        .subscribe();
    } else {
      return of(true);
    }
  }

  login(redirectPath: string = '/') {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        appState: { target: redirectPath },
        authorizationParams:{
          redirect_uri: this._config.redirectUri
        }
      });
    });
  }

  handleAuthCallback() {
    // Call when app reloads after user logs in with Auth0
    const params = window.location.search;
    if (params.includes('code=') && params.includes('state=')) {
      let targetRoute: string; // Path to redirect to after login processsed
      const authComplete$ = this.handleRedirectCallback$.pipe(
        // Have client, now call method to handle auth callback redirect
        tap((cbRes: any) => {
          // Get and set target redirect route from callback results
          targetRoute =
            cbRes.appState && cbRes.appState.target
              ? cbRes.appState.target
              : '/';
        }),
        concatMap(() =>
          // Redirect callback complete; get user and login status
          combineLatest([this.getUser$(), this.isAuthenticated$])
        )
      );
      // Subscribe to authentication completion observable
      // Response will be an array of user and login status
      authComplete$.subscribe(([user, loggedIn]) => {
        // Redirect to target route after callback processing
        this.router.navigate([targetRoute]);
      });
    }
  }

  logout() {
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        clientId: this._config.clientID,
        logoutParams: {
          returnTo: this._config.logoutUrl,
          federated: true,
        }
      });
    });
  }

  getTokenSilently(): Observable<any> {
    return this.auth0Client$.pipe(
      flatMap((client: Auth0Client) =>
        from(
          client.getTokenSilently({
            authorizationParams: {
              audience: this._config.audience,
              scope: this._config.scope,
              redirect_uri: this._config.redirectUri,
            }
          })
        )
      )
    );
  }
}
